import { Alert, Button, Text, TextInput } from '@ksyos/design-system';
import { useEffect, useMemo, useState } from 'react';
import type { IRequestGetOne } from '../../interfaces/requestGetOne';
import type { IProcedureInvitation } from '../../pages/customControls/GGZBther/v1/GgzbTherV1RequestRead';
import { CounselingMessageStatus } from '../../../../common/enums/counselingMessageStatus';
import { ThirdPartyMessageStatus } from '../../../../common/enums/thirdPartyMessageStatus';
import { ggzbTherV1NewEmailAdres } from '../../../../common/validation/customControls/mentalHealth';
import { fetchJson } from '../../../store/fetch';
import { translateKey } from '../../../translate';
import { getValidationErrorTranslation } from '../../../utils/getValidationErrorTranslation';
import { useAbortableFetch } from '../../../utils/hooks/useAbortableFetch';
import { useInterval } from '../../../utils/hooks/useInterval';
import { validate } from '../../../utils/validation';
import { FormLabel } from '../forms/FormLabel';
import { HorizontalFormGroup } from '../forms/HorizontalFormGroup';
import { InputContainer } from '../forms/InputContainer';
import { Modal } from '../modals/Modal';

interface IChangeEmailAddressForInvitationsButtonProps {
    emailadres: string;
    invitations: IProcedureInvitation[] | null;
    reloadRequest: () => Promise<void>;
    request: IRequestGetOne;
}

export function ChangeEmailAddressForInvitationsButton(props: IChangeEmailAddressForInvitationsButtonProps) {
    const baseState = useMemo(() => getBaseState(), []);
    const { request, emailadres, reloadRequest } = props;
    const [showModal, setShowModal] = useState(false);
    const [newEmailadres, setNewEmailadres] = useState<string | null>(emailadres);
    const [emailadresError, setEmailadresError] = useState<string>();
    const [counselingMessageStatus, setCounselingMessageStatus] = useState(baseState);
    const abortableFetch = useAbortableFetch();

    useEffect(() => {
        setNewEmailadres(emailadres);
    }, [showModal]);

    useInterval(async () => {
        if (counselingMessageStatus === CounselingMessageStatus.pending) {
            const result = await abortableFetch.fetchJson(
                `/api/therapieland/getCounselingMessageStatus/${request.id}`,
                'GET'
            );
            setCounselingMessageStatus(result.json.status);
        }
    }, 10000);

    if (!request.authorization?.canResendInvitations) {
        return null;
    }

    return (
        <>
            <Modal
                actionButtons={[
                    <Button
                        onClick={() => setShowModal(false)}
                        variant="outline"
                    >
                        {' '}
                        {translateKey('common.cancel')}
                    </Button>,
                    <Button
                        isDisabled={counselingMessageStatus !== CounselingMessageStatus.success}
                        onClick={async () => await send()}
                    >
                        {' '}
                        {translateKey('request.saveSend')}
                    </Button>,
                ]}
                isOpen={showModal}
                onClose={() => setShowModal(false)}
                size={'medium'}
                title={'E-mailadres aanpassen'}
            >
                <Text mb={4}>
                    Heeft je patiënt de uitnodiging niet ontvangen? Controleer het e-mailadres en verstuur de
                    uitnodiging voor de programma's nog een keer.
                </Text>
                <HorizontalFormGroup>
                    <FormLabel>E-mailadres</FormLabel>
                    <InputContainer>
                        <TextInput
                            error={getValidationErrorTranslation(emailadresError)}
                            onBlur={() => validateEmailadres()}
                            onChange={({ target: { value } }) => {
                                setEmailadresError(undefined);
                                setNewEmailadres(value);
                            }}
                            placeholder={'E-mailadres'}
                            value={newEmailadres || ''}
                        />
                    </InputContainer>
                </HorizontalFormGroup>
                {counselingMessageStatus === CounselingMessageStatus.pending && (
                    <Alert
                        appearance="info"
                        isLoading
                    >
                        {translateKey('changeEmailAddresssForInvitationModal.pending')}
                    </Alert>
                )}
                {counselingMessageStatus === CounselingMessageStatus.failed && (
                    <Alert appearance="warning">{translateKey('changeEmailAddresssForInvitationModal.failed')}</Alert>
                )}
                {counselingMessageStatus === CounselingMessageStatus.success && (
                    <Alert appearance="success">{translateKey('changeEmailAddresssForInvitationModal.success')}</Alert>
                )}
            </Modal>
            <Button
                onClick={() => setShowModal(true)}
                variant="outline"
            >
                {translateKey('request.action_changeEmailAddressInvitations')}
            </Button>
        </>
    );

    function getBaseState(): CounselingMessageStatus {
        if (!props.invitations) {
            return CounselingMessageStatus.pending;
        }

        let state = CounselingMessageStatus.success;
        for (const invitation of props.invitations) {
            if (invitation.thirdPartyMessageStatus === ThirdPartyMessageStatus.failed) {
                state = CounselingMessageStatus.failed;
                break;
            } else if (invitation.thirdPartyMessageStatus !== ThirdPartyMessageStatus.sent) {
                state = CounselingMessageStatus.pending;
            }
        }

        return state;
    }

    async function send() {
        const emailAdresIsValid = await validateEmailadres();
        if (!emailAdresIsValid) {
            return;
        }

        const result = await fetchJson('/api/therapieland/checkIsEmailUniqueForPatient', 'POST', {
            emailadres: newEmailadres,
            patientId: request.patientId,
        });
        if (!result.json.isUniqueEmailForPatient) {
            setEmailadresError('duplicateEmailTherapieland');

            return;
        }

        await fetchJson(`/api/requests/${request.id}/therapieland/resendInvitations`, 'POST', { newEmailadres });
        setShowModal(false);
        setCounselingMessageStatus(CounselingMessageStatus.pending);
        if (reloadRequest) {
            await reloadRequest();
        }
    }

    async function validateEmailadres() {
        setEmailadresError(undefined);

        if (emailadres === newEmailadres) {
            setEmailadresError('changeEmailAddress');

            return false;
        }

        const validationResult = validate({ schema: ggzbTherV1NewEmailAdres }, { newEmailadres });
        if (validationResult.errors.has('newEmailadres')) {
            setEmailadresError(validationResult.errors.get('newEmailadres'));

            return false;
        }

        return true;
    }
}
